import isEqual from 'react-fast-compare';

import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {MultiDocumentCellColors, TableCellEditorPosition} from 'notebook/tables/constants/CellConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import FormulaUtil, {CellInsertLoc} from 'notebook/tables/excess/FormulaUtil';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
// Prototype-less so that looking up a document named like an `Object.prototype` member finds nothing.
const NO_CELLS: MultiDocumentCellColors = Object.create(null);

export interface SpreadsheetStoreState {
  editingTable: string | null;
  editingDocumentName: string | null;
  formula: string;
  formulaHTML: string;
  typedValue: TypedValue | null;
  cellColors: MultiDocumentCellColors;
  selection: {cid: string; start: number; end: number} | null;
  cellInsertLoc: CellInsertLoc | null;
  cellEditorPosition: TableCellEditorPosition | null;
  fileId: string | null;
  index: TableIndex | null;
}

/**
 * Manages the formula currently being edited. Allows editing the formula for one table over all open
 * spreadsheet-containing documents (i.e. NoteEditor, StepsEditor)
 */
class SpreadsheetStore extends Store {
  activeNamespace!: string | null;
  activeTableName!: string | null;
  activeFileId!: string | null;
  activeIndex!: TableIndex | null;
  editingTable!: SpreadsheetStoreState['editingTable'];
  editingDocumentName!: SpreadsheetStoreState['editingDocumentName'];
  formula!: SpreadsheetStoreState['formula'];
  formulaHTML!: SpreadsheetStoreState['formulaHTML'];
  typedValue!: SpreadsheetStoreState['typedValue'];
  cellColors!: SpreadsheetStoreState['cellColors'];
  selection!: SpreadsheetStoreState['selection'];
  cellInsertLoc!: SpreadsheetStoreState['cellInsertLoc'];
  cellEditorPosition!: SpreadsheetStoreState['cellEditorPosition'];

  override resetState(): void {
    this.activeNamespace = null;
    this.activeTableName = null;
    this.activeFileId = null;
    this.activeIndex = null;
    this.editingTable = null;
    this.editingDocumentName = null;
    this.formula = '';
    this.formulaHTML = '';
    this.typedValue = null;
    this.cellColors = NO_CELLS;
    this.selection = null;
    this.cellInsertLoc = null;
    this.cellEditorPosition = null;
  }

  /**
   * Returns the current state of the spreadsheet store for the given namespace and table name.
   * If the namespace or tableName are not provided, then it will just return the value for whatever table
   * or namespace are currently active.
   */
  getState(namespace: string | null, tableName?: string, fileId?: string): SpreadsheetStoreState {
    // If `namespace` is not active, the document does not have a cell that is being actively edited.
    if (
      namespace !== this.activeNamespace ||
      (tableName && tableName !== this.activeTableName) ||
      (fileId && fileId !== this.activeFileId)
    ) {
      return {
        formula: '',
        formulaHTML: '',
        typedValue: null,
        index: null,
        cellColors: NO_CELLS,
        editingTable: null,
        editingDocumentName: null,
        selection: null,
        cellInsertLoc: null,
        cellEditorPosition: null,
        fileId: null,
      };
    }
    return {
      formula: this.formula,
      formulaHTML: this.formulaHTML,
      typedValue: this.typedValue,
      index: this.activeIndex,
      cellColors: this.cellColors,
      editingTable: this.editingTable,
      editingDocumentName: this.editingDocumentName,
      selection: this.selection,
      cellInsertLoc: this.cellInsertLoc,
      cellEditorPosition: this.cellEditorPosition,
      fileId: this.activeFileId,
    };
  }

  override payloadHandler(payload: FluxAction): boolean {
    switch (payload.actionType) {
      case EventConstants.SPREADSHEET_SET_FORMULA: {
        this.selection = payload.selection || null;
        this.activeNamespace = payload.tableNamespace;
        this.activeTableName = payload.tableName;
        this.activeFileId = payload.fileId;
        this.activeIndex = payload.index ?? null;
        this.editingTable = payload.editing ? payload.tableName : null;
        this.editingDocumentName = payload.editing ? payload.documentName : null;
        this.typedValue = payload.typedValue;
        if (payload.formula != null && payload.tableName != null) {
          const {formulaHTML, cellColors, cellInsertLoc} = FormulaUtil.getFormattedFormula(
            payload.tableName,
            payload.documentName,
            payload.formula,
            // TODO: `this.selection` is only updated when the formula updated, not when the cursor is moved
            // by the user, which means any formula rendering that is a function of selection will be stale,
            // unless the selection is kept up-to-date (i.e. component constantly pushes selection to Store).
            this.selection,
            payload.cellInsertMode
          );
          this.formula = payload.formula;
          this.formulaHTML = formulaHTML;
          if (!isEqual(cellColors, this.cellColors)) {
            this.cellColors = cellColors;
          }
          if (!isEqual(cellInsertLoc, this.cellInsertLoc)) {
            this.cellInsertLoc = cellInsertLoc;
          }
        } else {
          this.resetState();
        }
        this.emitChange();
        break;
      }
      case EventConstants.SPREADSHEET_SET_TYPED_VALUE: {
        this.typedValue = payload.typedValue;
        this.emitChange();
        break;
      }
      case EventConstants.SPREADSHEET_SET_CELL_EDITOR_POSITION: {
        this.cellEditorPosition = payload.cellEditorPosition || null;
        this.emitChange();
        break;
      }
      default:
        break;
    }
    return true;
  }
}

const spreadsheetStore = new SpreadsheetStore(AppDispatcher);

export default spreadsheetStore;
